/**
 * Pricing page — FIX 1 + FIX 2
 *
 * FIX 1: Active subscribers always go to Stripe Customer Portal for plan changes.
 *        Only free→paid is a fresh checkout.
 * FIX 2: Button labels reflect the user's current tier correctly:
 *        "Current Plan" (disabled) | "Upgrade" (primary) | "Downgrade" (secondary)
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile, isActiveSubscriber, TIER_ORDER } from '../hooks/useUserProfile';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';
import { useState } from 'react';
import { Navbar } from '../components/layout/Navbar';
import { usePurchaseCredits } from '../hooks/useCredits';
import { useCredits } from '../hooks/useCredits';

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: '',
    credits: '3 chapter previews lifetime',
    features: [
      '3 free chapter previews',
      'No credit card required',
      'All genres supported',
    ],
    tier: 'free' as const,
    popular: false,
  },
  {
    name: 'Starter',
    price: '$19',
    period: '/mo',
    credits: '2 books/month',
    features: [
      '2 full novels per month',
      'EPUB + DOCX export',
      'Cover generation (3 variations)',
      'KDP publishing walkthrough',
      'Email support',
    ],
    tier: 'starter' as const,
    popular: false,
  },
  {
    name: 'Creator',
    price: '$49',
    period: '/mo',
    credits: '5 books/month',
    features: [
      '5 full novels per month',
      'Everything in Starter',
      'Series management',
      'Author persona generator',
      'Most Creative mode',
      'Priority generation',
    ],
    tier: 'creator' as const,
    popular: true,
  },
  {
    name: 'Publisher',
    price: '$99',
    period: '/mo',
    credits: '15 books/month',
    features: [
      '15 full novels per month',
      'Everything in Creator',
      'Bulk generation queue',
      'Priority support',
      'Early access to new features',
    ],
    tier: 'publisher' as const,
    popular: false,
  },
];

type ButtonVariant = 'current' | 'upgrade' | 'downgrade' | 'get-started';

function getPlanButtonConfig(
  planTier: string,
  userTier: string | null | undefined,
  isLoggedIn: boolean,
  hasActiveSub: boolean,
): { label: string; variant: ButtonVariant; disabled: boolean } {
  if (!isLoggedIn) {
    return { label: 'Get Started', variant: 'get-started', disabled: false };
  }

  const effectiveUserTier = userTier ?? 'free';
  const userOrder = TIER_ORDER[effectiveUserTier] ?? 0;
  const planOrder = TIER_ORDER[planTier] ?? 0;

  if (planTier === effectiveUserTier && (!hasActiveSub || planTier === 'free')) {
    return { label: 'Current Plan', variant: 'current', disabled: true };
  }

  if (planTier === effectiveUserTier && hasActiveSub) {
    return { label: 'Current Plan', variant: 'current', disabled: true };
  }

  if (planOrder > userOrder) {
    return { label: 'Upgrade', variant: 'upgrade', disabled: false };
  }

  // planOrder < userOrder
  return { label: 'Downgrade', variant: 'downgrade', disabled: false };
}

export function Pricing() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { data: profile } = useUserProfile();
  const { data: credits } = useCredits();
  const purchaseCredits = usePurchaseCredits();
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [loadingPortal, setLoadingPortal] = useState(false);

  const userTier = profile?.subscriptionTier ?? null;
  const hasActiveSub = isActiveSubscriber(profile);

  const goToPortal = async () => {
    setLoadingPortal(true);
    try {
      const res = await api.post<ApiSuccess<{ portalUrl: string }>>('/subscriptions/portal');
      window.location.href = res.data.data.portalUrl;
    } catch {
      alert('Failed to open billing portal. Please try again.');
      setLoadingPortal(false);
    }
  };

  const handlePlanClick = async (planTier: string) => {
    // Not logged in → signup
    if (!isAuthenticated) {
      void navigate('/signup');
      return;
    }

    // Free plan is never a checkout
    if (planTier === 'free') {
      // If active subscriber wants to downgrade to free → portal (cancel there)
      if (hasActiveSub) {
        await goToPortal();
      }
      return;
    }

    // Active subscriber changing plan → always use Stripe Customer Portal
    if (hasActiveSub) {
      await goToPortal();
      return;
    }

    // Free user upgrading to paid → new checkout session
    setLoadingTier(planTier);
    try {
      const res = await api.post<ApiSuccess<{ checkoutUrl: string }>>('/subscriptions/checkout', {
        tier: planTier,
      });
      window.location.href = res.data.data.checkoutUrl;
    } catch {
      alert('Failed to start checkout. Please try again.');
      setLoadingTier(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant={isAuthenticated ? 'authed' : 'public'} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl font-bold text-foreground mb-3">Simple, transparent pricing</h1>
          <p className="text-muted-foreground text-base sm:text-lg">Start free. Upgrade when you&apos;re ready to publish.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {plans.map((plan) => {
            const { label, variant, disabled } = getPlanButtonConfig(
              plan.tier,
              userTier,
              isAuthenticated,
              hasActiveSub,
            );

            const isLoading =
              loadingTier === plan.tier || (loadingPortal && !disabled);

            const buttonClassName = (() => {
              const base = 'w-full py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50';
              if (variant === 'current') {
                return `${base} border border-border text-muted-foreground cursor-default`;
              }
              if (variant === 'upgrade' || variant === 'get-started') {
                return plan.popular
                  ? `${base} bg-primary text-primary-foreground hover:bg-primary/90`
                  : `${base} bg-primary/80 text-primary-foreground hover:bg-primary/70`;
              }
              // downgrade — secondary/subtle
              return `${base} border border-border text-muted-foreground hover:bg-accent`;
            })();

            return (
              <div
                key={plan.name}
                className={`p-6 border rounded-lg bg-card flex flex-col ${
                  plan.popular ? 'border-primary ring-1 ring-primary' : 'border-border'
                }`}
              >
                {plan.popular && (
                  <div className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full w-fit mb-3">
                    Most Popular
                  </div>
                )}
                <h2 className="text-xl font-bold text-foreground mb-1">{plan.name}</h2>
                <div className="flex items-end gap-0.5 mb-1">
                  <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                <p className="text-sm text-primary font-medium mb-4">{plan.credits}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="text-green-400 mt-0.5">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => { if (!disabled) void handlePlanClick(plan.tier); }}
                  disabled={disabled || isLoading}
                  className={buttonClassName}
                >
                  {isLoading ? 'Loading…' : label}
                </button>
              </div>
            );
          })}
        </div>

        {/* Credit pack — only shown to logged-in users */}
        {isAuthenticated && <div className="max-w-md mx-auto p-6 border border-border rounded-lg bg-card text-center">
          <h3 className="font-semibold text-foreground mb-1">Need extra books?</h3>
          <p className="text-muted-foreground text-sm mb-1">
            Credit Pack — 10 credits for $9.99 (one-time purchase)
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            Use credits to generate chapters and covers beyond your plan limit.
          </p>
          {isAuthenticated && credits !== undefined && (
            <p className="text-xs text-muted-foreground mb-3">
              Current balance: <span className="font-semibold text-foreground">{credits.creditBalance} credits</span>
            </p>
          )}
          <button
            onClick={() => {
              if (!isAuthenticated) { void navigate('/signup'); return; }
              purchaseCredits.mutate();
            }}
            disabled={purchaseCredits.isPending}
            className="px-6 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors text-sm font-medium disabled:opacity-50"
          >
            {purchaseCredits.isPending ? 'Loading…' : 'Buy 10 Credits for $9.99'}
          </button>
        </div>}
      </main>
    </div>
  );
}
